import { elementAttributes } from "../html-attributes";
import { isBlankColor, parseStyleAttr } from "../html-parse";
import { EMPTY_STYLE_MAP, type StyleMap } from "../shared-block-styles";
import { EmailBlockDefinition } from "../types";

import { LuMinus } from "react-icons/lu";

import { dividerEmailRenderer } from "./email-renderer";
import { DividerTiptapView } from "./tiptap-view";

export interface DividerAttrs extends Record<string, unknown> {
  color: string;
  thickness: string;
  /** Inline styles as the markup wrote them — the block's style store. */
  style: StyleMap;
  /** Attributes the block does not otherwise model, kept rather than dropped. */
  attributes: Record<string, string>;
}

/**
 * A horizontal rule — the separator designed emails use between sections.
 *
 * Kept out of `blockRegistry` (like the quote block) so the block picker is
 * unchanged: this exists so a pasted template's `<hr>` survives the trip into
 * the composer, rather than vanishing and leaving the sections it separated
 * running together.
 */
export const dividerBlock: EmailBlockDefinition<DividerAttrs> = {
  name: "emailDivider",
  label: "Divider",
  icon: LuMinus,
  category: "layout",
  schema: {
    attrs: {
      color: { default: "" },
      thickness: { default: "" },
      style: { default: EMPTY_STYLE_MAP },
      attributes: { default: {} },
    },
    content: undefined,
    group: "emailBlock",
    draggable: false,
    isolating: true,
  },

  // `border: none; border-top: 1px solid #ccc` is how an email draws a rule, so
  // the visible line is the *top* border rather than the element's own border.
  parseHTML: () => [
    {
      tag: "hr",
      getAttrs: (node) => {
        const el = node as HTMLElement;
        const color = el.style.borderTopColor || el.style.borderColor || "";
        const thickness = el.style.borderTopWidth || el.style.borderWidth || "";
        return {
          color: isBlankColor(color) ? "" : color,
          thickness,
          style: parseStyleAttr(el),
          attributes: elementAttributes(el),
        };
      },
    },
  ],

  tiptapComponent: DividerTiptapView,
  emailRenderer: dividerEmailRenderer,
};
